/**
 * This file is maintained by Stencil, do not directly modify this file
 */
'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from 'react';
import { useCommonData } from '@udemy-v2/common-data';
import { createAmplitudeTrackingCallback } from '@udemy-v2/amplitude';
import { useTracker } from '@udemy-v2/event-tracking';
import { initializeVendors } from '@/lib/vendors';
import { useI18n } from '@udemy-v2/i18n';
import { GoogleTagManager } from '@udemy-v2/gtag';

/**
 * Props for the Vendors component
 */
export interface VendorsProps {
  children?: React.ReactNode;
}

/**
 * Vendors component that initializes third-party services and renders vendor components
 * Uses common data from context to set up vendors like Sentry
 * Renders vendor components like GoogleTagManager
 *
 * @component
 * @param {Readonly<VendorsProps>} props - Component props with readonly constraint
 * @returns {JSX.Element} Vendor components and children wrapped with vendor initialization
 */
export function Vendors(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const commonData = useCommonData();
  const {
    locale
  } = useI18n();
  const tracker = useTracker();
  const amplitudeInitialized = useRef(null);
  if (amplitudeInitialized.current == null) {
    tracker.registerCallback(createAmplitudeTrackingCallback() as Parameters<typeof tracker.registerCallback>[0]);
    amplitudeInitialized.current = true;
  }
  const vendorsInitialized = useRef(false);
  let t1;
  let t2;
  if ($[0] !== commonData || $[1] !== locale) {
    t1 = () => {
      if (commonData.page?.isLoading === false && !vendorsInitialized.current) {
        ;
        try {
          initializeVendors(commonData, locale);
          vendorsInitialized.current = true;
        } catch (t3) {
          const error = t3;
          console.error("Failed to initialize vendors:", error);
        }
      }
    };
    t2 = [commonData, locale];
    $[0] = commonData;
    $[1] = locale;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GoogleTagManager />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children) {
    t4 = <>{t3}{children}</>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}